import React, { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../context/AuthContext'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import './ChangePassword.css'
import logo from '../assets/images/nesim-logo.jpg'
import { post } from '../api'

function checkPasswordRules(pw) {
  return {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    number: /\d/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
  }
}

export default function ChangePassword() {
  const { mustChangePassword, logout } = useContext(AuthContext)
  const navigate = useNavigate()

  const [oldPwd, setOldPwd] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')
  const [showOld, setShowOld] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [rules, setRules] = useState({
    length: false,
    upper: false,
    lower: false,
    number: false,
    special: false
  })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!mustChangePassword) navigate('/', { replace: true })
  }, [mustChangePassword, navigate])

  useEffect(() => {
    setRules(checkPasswordRules(newPwd))
  }, [newPwd])

  const passedRules = Object.values(rules).filter(Boolean).length
  const strength = passedRules

  async function handleSubmit(e) {
    e.preventDefault()
    if (submitting) return

    setError('')
    setSuccess('')

    if (newPwd !== confirmPwd) {
      setError('New password and confirmation do not match')
      return
    }

    if (oldPwd === newPwd) {
      setError('New password must be different from current')
      return
    }

    if (passedRules < 5) {
      setError('New password does not meet all requirements')
      return
    }

    setSubmitting(true)
    try {
      const data = await post('/auth/change-password', {
        oldPassword: oldPwd,
        newPassword: newPwd
      })

      setSuccess(data.message || 'Password updated successfully')
      setTimeout(() => {
        logout()
        navigate('/login', { replace: true })
      }, 1500)
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="change-password-page">
      <div className="change-container">
        <img src={logo} alt="NESIM Logo" className="change-logo" />
        <p className="tagline">Think Brewing solutions, think Nesim</p>
        <form className="change-password-form" onSubmit={handleSubmit}>
          <h2>Change Your Password</h2>
          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}

          {/* Current Password */}
          <label htmlFor="oldPwd">Current Password</label>
          <div className="input-wrapper">
            <input
              id="oldPwd"
              type={showOld ? 'text' : 'password'}
              value={oldPwd}
              onChange={e => setOldPwd(e.target.value)}
              required
              autoComplete="current-password"
              disabled={submitting}
            />
            <button type="button" onClick={() => setShowOld(v => !v)} disabled={submitting}>
              {showOld ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {/* New Password */}
          <label htmlFor="newPwd">New Password</label>
          <div className="input-wrapper">
            <input
              id="newPwd"
              type={showNew ? 'text' : 'password'}
              value={newPwd}
              onChange={e => setNewPwd(e.target.value)}
              required
              autoComplete="new-password"
              disabled={submitting}
            />
            <button type="button" onClick={() => setShowNew(v => !v)} disabled={submitting}>
              {showNew ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {/* Confirm Password */}
          <label htmlFor="confirmPwd">Confirm New Password</label>
          <div className="input-wrapper">
            <input
              id="confirmPwd"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPwd}
              onChange={e => setConfirmPwd(e.target.value)}
              required
              autoComplete="new-password"
              disabled={submitting}
            />
            <button type="button" onClick={() => setShowConfirm(v => !v)} disabled={submitting}>
              {showConfirm ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {/* Strength Meter + Rules */}
          {newPwd && (
            <>
              <div className="strength-meter">
                <div className="meter-bar" style={{ width: `${(strength / 5) * 100}%` }}></div>
                <div className="strength-label">
                  {strength < 2 ? 'Too weak' : strength < 4 ? 'Fair' : 'Strong'}
                </div>
              </div>

              <ul className="password-rules">
                <li className={rules.length ? 'valid' : 'invalid'}>
                  {rules.length ? '✔' : '✘'} At least 8 characters
                </li>
                <li className={rules.upper ? 'valid' : 'invalid'}>
                  {rules.upper ? '✔' : '✘'} At least one uppercase letter
                </li>
                <li className={rules.lower ? 'valid' : 'invalid'}>
                  {rules.lower ? '✔' : '✘'} At least one lowercase letter
                </li>
                <li className={rules.number ? 'valid' : 'invalid'}>
                  {rules.number ? '✔' : '✘'} At least one number
                </li>
                <li className={rules.special ? 'valid' : 'invalid'}>
                  {rules.special ? '✔' : '✘'} At least one special symbol
                </li>
              </ul>
            </>
          )}

          <button
            type="submit"
            disabled={
              submitting ||
              !oldPwd ||
              !newPwd ||
              !confirmPwd ||
              newPwd !== confirmPwd ||
              oldPwd === newPwd ||
              passedRules < 5
            }
          >
            {submitting ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
